/**
 * `listings.compliance` (config-plan §4a): the Listings compliance knobs.
 *
 * Since CF-11 the values live in `app_settings` (the migration copied the
 * `listing_settings` row into these five keys): the Listings statements read
 * them in their own SQL (API data/listings-compliance.ts, settingSql), and
 * they are edited on Settings → App settings and on Settings → Listing task
 * templates (both render this group; PUT /api/listings/settings is an adapter
 * over the same save).
 *
 * Who: editPermission feature:settings_listings (seeded to Admin and Super
 * Admin; admins always pass) changes them; readPermission
 * feature:listings_templates lets everyone who opens Listing task templates
 * see them read-only there, as they always could.
 *
 * Types, bounds and defaults are those of `listing_settings` (its CHECKs, the
 * DB defaults, as its migrations declared them; CF-14 dropped the table) and PUT /api/listings/settings (LISTING_COMPLIANCE_LIMITS, at
 * most 50 unique roles); tests/settings.test.ts keeps them equal.
 *
 * Three knobs were never listing_settings columns: audit_max_age_days (M202, the
 * Master Listing Sheet's audit health) and M227's task_digest_enabled /
 * task_digest_role_ids (the 07:00 weekday email of each recipient's own overdue
 * and due-today tasks, off by default; API services/listings/listings-task-digest.ts).
 * They sit here so Listing task templates shows them with the other compliance
 * knobs; PUT /api/listings/settings does not take them (Settings → App
 * settings, or PUT /api/settings/listings.compliance).
 */
import { z } from 'zod';
import { LISTING_COMPLIANCE_LIMITS } from '../../limits/listings.js';
import { SIGN_REQUEST_LIMITS } from '../../listings/sign-requests.js';
import { defineGroup } from '../define.js';
const days = LISTING_COMPLIANCE_LIMITS.expiryWarningDays;
const hours = LISTING_COMPLIANCE_LIMITS.takedownWindowHours;
export const REMINDER_ROLES_MAX = 50;
export const listingsCompliance = defineGroup('listings.compliance', {
    label: 'Listing compliance',
    editPermission: 'feature:settings_listings',
    readPermission: ['feature:listings_templates'],
    settings: {
        expiry_warning_days: {
            type: z.number().int().min(days.min).max(days.max),
            default: 30,
            label: 'Expiry warning (days)',
            help: 'Warn when an active agreement expires within this many days (projects list, board, My work, the broker card).',
        },
        takedown_window_hours: {
            type: z.number().int().min(hours.min).max(hours.max),
            default: 24,
            label: 'Takedown window (hours)',
            help: 'How long a listing may stay up once its sale or lease is linked.',
        },
        silence_warnings_when_fulfilled: {
            type: z.boolean(),
            default: true,
            label: 'Stop expiry warnings once every listing is finished',
            help: 'When every listing on an agreement is Transacted, Withdrawn or Taken down, the agreement shows no expiry warning and is left out of the reminder email.',
        },
        reminders_enabled: {
            type: z.boolean(),
            default: false,
            label: 'Send the daily reminder email',
            help: 'A daily email of the tasks and agreements that need attention, sent to the people in the roles below.',
        },
        reminder_role_ids: {
            type: z
                .array(z.string().uuid())
                .max(REMINDER_ROLES_MAX)
                .refine((ids) => new Set(ids).size === ids.length, { message: 'each role once' }),
            default: [],
            label: 'Reminder email recipients (roles)',
            help: 'Everyone in these roles receives the daily reminder email.',
            control: 'roles',
        },
        audit_max_age_days: {
            type: z.number().int().min(1).max(730),
            default: 90,
            label: 'Audit due after (days)',
            help: 'An active agreement not audited for more than this many days (or, never audited, created more than this many days ago) shows as audit overdue.',
        },
        task_digest_enabled: {
            type: z.boolean(),
            default: false,
            label: 'Send the morning task email',
            help: 'At 7 am on weekdays, each person in the roles below gets an email of their own overdue and due-today listing tasks (nobody with nothing due gets one).',
        },
        task_digest_role_ids: {
            type: z
                .array(z.string().uuid())
                .max(REMINDER_ROLES_MAX)
                .refine((ids) => new Set(ids).size === ids.length, { message: 'each role once' }),
            default: [],
            label: 'Morning task email recipients (roles)',
            help: 'Everyone in these roles receives the morning email of their own tasks.',
            control: 'roles',
        },
    },
    validate: (merged) => merged.reminders_enabled && merged.reminder_role_ids.length === 0
        ? { code: 'REMINDER_RECIPIENTS_REQUIRED', message: 'choose at least one role to receive the reminder email' }
        : merged.task_digest_enabled && merged.task_digest_role_ids.length === 0
            ? { code: 'TASK_DIGEST_RECIPIENTS_REQUIRED', message: 'choose at least one role to receive the morning task email' }
            : null,
    refs: {
        reminder_role_ids: { table: 'roles', code: 'UNKNOWN_ROLE' },
        task_digest_role_ids: { table: 'roles', code: 'UNKNOWN_ROLE' },
    },
});
/**
 * `listings.workload` (M22a): the admin Team workload page's knob. One so far:
 *
 *   stale_task_days   an open task overdue by MORE than this many days is "stuck"
 *                     (GET /api/listings/team-workload, API data/listings-team-workload.ts)
 *
 * Its own group, not a knob of `listings.compliance`: that group is the retired
 * `listing_settings` row, key for key (tests/settings.test.ts). Same edit key
 * (feature:settings_listings: Admin and Super Admin; admins always pass); no
 * readPermission, as the page that uses it is admin-only. Edited on Settings →
 * App settings. Default 3 (M22).
 */
export const listingsWorkload = defineGroup('listings.workload', {
    label: 'Listings team workload',
    editPermission: 'feature:settings_listings',
    settings: {
        stale_task_days: {
            type: z.number().int().min(1).max(365),
            default: 3,
            label: 'Stuck after (days overdue)',
            help: 'On Team workload, an open task overdue by more than this many days is listed as stuck.',
        },
    },
});
/**
 * `listings.expiry` (M29a): what the daily expiry job does to an agreement's
 * listings. One knob so far:
 *
 *   takedown_on_expiry   when an agreement expires, each of its ACTIVE listings
 *                        becomes Withdrawn and gets the Takedown template's
 *                        tasks (its live platform posts are listed on them), one
 *                        feed row per listing (API listings-expiry-takedown.ts).
 *                        Renew / reopen puts those listings back. Default on.
 *
 * Its own group, not a knob of `listings.compliance`: that group is the retired
 * `listing_settings` row, key for key (tests/settings.test.ts). Same edit key as
 * the compliance knobs (feature:settings_listings: Admin and Super Admin; admins
 * always pass) and the same read key (feature:listings_templates). Edited on
 * Settings → App settings.
 */
export const listingsExpiry = defineGroup('listings.expiry', {
    label: 'Listing agreement expiry',
    editPermission: 'feature:settings_listings',
    readPermission: ['feature:listings_templates'],
    settings: {
        takedown_on_expiry: {
            type: z.boolean(),
            default: true,
            label: 'Take listings down when their agreement expires',
            help: "When an agreement expires, each of its active listings becomes Withdrawn and gets the Takedown tasks (they list the platforms it is still posted on). Renewing or reopening the agreement puts those listings back to Active.",
        },
    },
});
/**
 * `listings.signage` (sign requests, migration 20261017234905): one knob so far:
 *
 *   sr_start_number   the first SR number the app gives (SR-0001 style). Sign
 *                     requests are numbered in sequence: the next is the larger
 *                     of (the highest so far + 1) and this, so setting it to the
 *                     Sign Request spreadsheet's next number continues that
 *                     sequence (API services/listings/listings-sign-request-writes.ts).
 *                     Lowering it never reuses a number. Default 1.
 *
 * Its own group, not a knob of `listings.compliance` (that group is the retired
 * `listing_settings` row, key for key). Same edit and read keys as the
 * compliance knobs; edited on Settings → App settings.
 */
export const listingsSignage = defineGroup('listings.signage', {
    label: 'Listing sign requests',
    editPermission: 'feature:settings_listings',
    readPermission: ['feature:listings_templates'],
    settings: {
        sr_start_number: {
            type: z.number().int().min(1).max(SIGN_REQUEST_LIMITS.maxSrNumber),
            default: 1,
            label: 'First sign request number (SR #)',
            help: 'New sign requests are numbered from here, or from the highest SR number so far + 1 if that is higher. Set it to the Sign Request spreadsheet’s next number to continue its sequence.',
        },
    },
});
//# sourceMappingURL=listings.js.map